"use client";
import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import styles from "./SearchInput.module.css";
import { Search } from "lucide-react";

interface Suggestion {
  id?: number;
  type: "song" | "work" | "playlist" | "composer";
  title: string;
  slug?: string;
  imageUrl?: string;
  plays?: number;
}


interface SearchInputProps {
  autoFocus?: boolean;
  onSearch?: (query: string) => void;
}

export default function SearchInput({ autoFocus, onSearch }: SearchInputProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const router = useRouter();

  // закрытие подсказок при клике вне
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    router.push(`/search?q=${encodeURIComponent(q)}`);
    setSuggestions([]);
    if (onSearch) onSearch(q); // 👈 закрываем оверлей
  };

  useEffect(() => {
    if (q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    const fetchSuggestions = async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        setSuggestions(data.results || []);
      } catch (err) {
        console.error("Autocomplete error:", err);
        setSuggestions([]);
      }
    };
    fetchSuggestions();
  }, [q]);

  const handleClick = (item: Suggestion) => {
    setQ(item.title);
    setSuggestions([]);
    if (item.type === "song" && item.slug) {
      router.push(`/song/${item.slug}`);
    } else if (item.type === "playlist" && item.slug) {
      router.push(`/playlist/${item.slug}`);
    } else if (item.type === "work" && item.slug) {
      router.push(`/work/${item.slug}`);
    } else {
      router.push(`/search?q=${encodeURIComponent(item.title)}`);
    }
    if (onSearch) onSearch(item.title);
  };
  const getThumbnailSrc = (imageUrl?: string) => {
    if (!imageUrl) return '/placeholder.jpg';

    return imageUrl.replace(
      '/covers/',
      '/covers/220/'
    );
  };
  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div ref={wrapperRef} className={styles.inputWrapper}>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by composer or title, or try a mood or era"
          className={styles.input}
          autoComplete="off"
          autoFocus={autoFocus}
        />

        {suggestions.length > 0 && (
          <div className={styles.suggestions}>
            {suggestions.filter(s => s.type === "composer").length > 0 && (
              <>
                <div className={styles.groupLabel}>Composers</div>
                <ul>
                  {suggestions.filter(s => s.type === "composer").map((c, i) => (
                    <li key={`composer-${i}`} onClick={() => handleClick(c)} className={styles.suggestionItem}>
                      <div className={styles.itemContent}>
                        <Search className={styles.icon} />
                        <span className={styles.label}>{c.title}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {suggestions.filter(s => s.type === "playlist").length > 0 && (
              <>
                <div className={styles.groupLabel}>Playlists</div>
                <ul>
                  {suggestions
                    .filter(s => s.type === "playlist")
                    .slice(0, 4) // 👈 максимум 5 подсказок
                    .map((p, i) => (
                      <li
                        key={`playlist-${i}`}
                        onClick={() => handleClick(p)}
                        className={styles.suggestionItem}
                      >
                        <div className={styles.itemContent}>
                          {p.imageUrl && (
                            <img
                              src={getThumbnailSrc(p.imageUrl)}
                              alt={p.title}
                              className={styles.thumbnail}
                            />
                          )}
                          <span className={styles.label}>{p.title}</span>
                        </div>
                      </li>
                    ))}
                </ul>
              </>
            )}

            {suggestions.filter(s => s.type === "work").length > 0 && (
              <>
                <div className={styles.groupLabel}>Works</div>
                <ul>
                  {suggestions
                    .filter(s => s.type === "work")
                    .slice(0, 4) // 👈 максимум 5 подсказок
                    .map((w, i) => (
                      <li
                        key={`work-${i}`}
                        onClick={() => handleClick(w)}
                        className={styles.suggestionItem}
                      >
                        <div className={styles.itemContent}>
                          {w.imageUrl && (
                            <img
                              src={getThumbnailSrc(w.imageUrl)}
                              alt={w.title}
                              className={styles.thumbnail}
                            />
                          )}
                          <span className={styles.label}>{w.title}</span>
                        </div>
                      </li>
                    ))}
                </ul>
              </>
            )}


            {suggestions.filter(s => s.type === "song").length > 0 && (
              <>
                <div className={styles.groupLabel}>Songs</div>
                <ul>
                  {suggestions
                    .filter(s => s.type === "song")
                    .slice(0, 4) // 👈 максимум 5 подсказок
                    .map((t, i) => (
                      <li
                        key={`song-${i}`}
                        onClick={() => handleClick(t)}
                        className={styles.suggestionItem}
                      >
                        <div className={styles.itemContent}>
                          {t.imageUrl && (
                            <img
                              src={getThumbnailSrc(t.imageUrl)}
                              alt={t.title}
                              className={styles.thumbnail}
                            />
                          )}
                          <span className={styles.label}>{t.title}</span>
                        </div>
                      </li>
                    ))}
                </ul>
              </>
            )}

          </div>
        )}
      </div>
      <button type="submit" className={styles.button}>
        Search
      </button>
    </form>
  );
}
